import React, { useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, EyeOff, UserX } from "lucide-react";
import { GlassButton } from "@/components/GlassBits";
import { Avatar } from "@/components/community/ChatList";

const OPTIONS = [
  { key: "last_seen", label: "En línea y última vez", hint: "Quién puede ver si estás conectado" },
  { key: "photo", label: "Mi foto de perfil", hint: "Quién puede ver tu foto" },
  { key: "bio", label: "Mi biografía y correo", hint: "Quién puede ver tus datos de perfil" },
  { key: "who_can_message", label: "Quién puede escribirme", hint: "Mensajes privados nuevos" },
  { key: "who_can_add", label: "Quién puede agregarme a grupos", hint: "Invitaciones a grupos" },
];

const PROFILE_OPTIONS = [
  { key: "wall", label: "Mi muro (mis publicaciones)" },
  { key: "photos", label: "Mis fotos" },
  { key: "friends_list", label: "Mi lista de amigos" },
];

const VISIBILITY = [
  { key: "all", label: "Todos" },
  { key: "friends", label: "Solo amigos" },
  { key: "nobody", label: "Nadie" },
];

const Switch = ({ on, onToggle, testId }) => (
  <button data-testid={testId} onClick={onToggle}
    className={`w-11 h-6 rounded-full border transition-colors relative shrink-0 ${
      on ? "bg-red-600/70 border-red-400/60" : "bg-white/8 border-white/15"
    }`}>
    <span className={`absolute top-0.5 w-4.5 h-4.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${
      on ? "left-[22px]" : "left-0.5"
    }`} />
  </button>
);

export const UserPrivacy = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await userApi.get("/community/privacy");
      setData(data);
    } catch (e) { toast.error(apiError(e)); setData({ privacy: {}, blocked: [] }); }
  };

  useEffect(() => { load(); }, []);

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data: res } = await userApi.patch("/community/privacy", body);
      setData((d) => ({ ...d, privacy: res.privacy }));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const unblock = async (key) => {
    try {
      await userApi.delete(`/community/block/${key}`);
      toast.success("Desbloqueado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-red-400" />;
  const p = data.privacy;

  return (
    <div className="space-y-5 max-w-xl" data-testid="user-privacy">
      <div className="glass-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold mb-1">
          <EyeOff className="w-4 h-4 text-red-400" /> Privacidad
        </div>
        <p className="text-xs text-white/50 mb-5">
          Activa el candado para ocultar cada dato. {saving && "Guardando…"}
        </p>
        <div className="space-y-4">
          {OPTIONS.map((o) => (
            <div key={o.key} className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-white">{o.label}</div>
                <div className="text-[11px] text-white/45">{o.hint}</div>
              </div>
              <Switch testId={`privacy-${o.key}`} on={p[o.key] === "nobody"}
                onToggle={() => patch({ [o.key]: p[o.key] === "nobody" ? "all" : "nobody" })} />
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 border-t border-white/8 pt-4">
            <div>
              <div className="text-sm text-white">Ocultar confirmaciones de lectura</div>
              <div className="text-[11px] text-white/45">
                Si las ocultas, tampoco verás cuándo leen tus mensajes
              </div>
            </div>
            <Switch testId="privacy-read_receipts" on={p.read_receipts === false}
              onToggle={() => patch({ read_receipts: !(p.read_receipts === false) ? false : true })} />
          </div>
        </div>
      </div>

      <div className="glass-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold mb-1">
          <EyeOff className="w-4 h-4 text-red-400" /> Privacidad de mi perfil
        </div>
        <p className="text-xs text-white/50 mb-5">
          Decide quién entra a tu perfil y ve lo que publicas.
        </p>
        <div className="space-y-4">
          {PROFILE_OPTIONS.map((o) => (
            <div key={o.key} className="flex items-center justify-between gap-4">
              <div className="text-sm text-white">{o.label}</div>
              <div className="flex gap-1.5 shrink-0">
                {VISIBILITY.map((v) => (
                  <button key={v.key} data-testid={`privacy-${o.key}-${v.key}`}
                    onClick={() => patch({ [o.key]: v.key })}
                    className={`glass-btn ${(p[o.key] || "friends") === v.key ? "glass-btn-red" : ""} px-2.5 py-1.5 rounded-xl text-[11px]`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold mb-3">
          <UserX className="w-4 h-4 text-red-400" /> Bloqueados
        </div>
        {data.blocked.length === 0 ? (
          <p className="text-xs text-white/50">No tienes a nadie bloqueado.</p>
        ) : (
          <div className="space-y-2">
            {data.blocked.map((b) => (
              <div key={b.key} className="flex items-center justify-between gap-3"
                data-testid={`blocked-${b.key}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar person={b} size={32} />
                  <span className="text-sm text-white truncate">{b.name}</span>
                </div>
                <GlassButton tone="ghost" className="w-auto px-3 py-1.5 text-xs"
                  data-testid={`unblock-${b.key}`} onClick={() => unblock(b.key)}>
                  Desbloquear
                </GlassButton>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPrivacy;
